import type { LucideIcon } from "lucide-react";
import {
  Calendar, LifeBuoy, Users, Plug, Cpu, LineChart, Network,
  Brain, Activity,
  BookOpen, Percent, FileText, Wallet,
  Truck, Banknote, Calculator, HandCoins,
  Star, ListChecks, FolderKanban, CalendarRange,
  RefreshCw, BellRing,
  Contact, Briefcase,
} from "lucide-react";

export type AreaKey = "ops" | "dev" | "admin" | "crm";

export type AreaModule = {
  key: string;
  label: string;
  icon: LucideIcon;
  description?: string;
  href?: string;
  comingSoon?: boolean;
  // Card TOLTA dall'elenco ma non dal registro: la voce resta qui con la sua
  // rotta e la sua descrizione, pronta a tornare. Diverso da `comingSoon`, che
  // la mostra spenta: `nascosta` non la mostra affatto. Si usa per cio' che
  // esiste ma che oggi non si vuole offrire — cancellare la riga farebbe
  // perdere il lavoro fatto e la memoria del perche' c'era.
  nascosta?: boolean;
  // Se valorizzato, la card compare SOLO se l'org ha l'entitlement di questo
  // module_code abilitato (da /platform). Assente = card statica (sempre visibile).
  requiresEntitlement?: string;
  // Gating per-utente (oltre all'entitlement org): se true, la card è visibile a
  // tutti gli utenti dell'area admin ma NON cliccabile finché l'utente non ha il
  // toggle del modulo attivo (ruolo per-utente da /platform). Il `requiresEntitlement`
  // corrispondente deve avere `user_has_access` distinto da `org_enabled` in
  // /me/access (moduli mappati nel BFF: PRACTICES). Assente = card cliccabile per
  // ogni utente dell'org che ha l'entitlement.
  perUserGated?: boolean;
};

export type AreaSection = {
  key: string;
  label: string;
  caption?: string;
  description?: string;
  modules: AreaModule[];
};

const ADMIN_OPS_MODULES: AreaModule[] = [
  { key: "travel-practice",       label: "Pratiche",              icon: Briefcase,     href: "/admin/practices",     description: "Analytics su volumi, revenue e conversion.", requiresEntitlement: "PRACTICES", perUserGated: true },
  { key: "travel-catalog",        label: "Travel Catalog",        icon: BookOpen,      href: "/admin/catalog",       description: "Gestione strutturata di servizi e pacchetti.", comingSoon: true },
  { key: "travel-commissions",    label: "Travel Commissions",    icon: Percent,       href: "/admin/commissions",   description: "Schemi commissioni, regole partner, tier e payout.", nascosta: true },
  // Pricing e Contract Room non hanno piu' una card: i listini si creano e si
  // gestiscono DENTRO l'anagrafica del fornitore/canale (area "Contratti"), che
  // e' il posto dove l'operatore li cerca. /admin/pricing redirige a /admin/directory.
  { key: "directory",             label: "Anagrafiche",           icon: Contact,       href: "/admin/directory",     description: "Fornitori, clienti e canali — e i loro contratti: listini, tariffe e supplementi.", requiresEntitlement: "DIRECTORY", perUserGated: true },
];

const ADMIN_TEAM_MODULES: AreaModule[] = [
  { key: "tasks",     label: "Tasks",     icon: ListChecks,    href: "/admin/tasks",     description: "Lista task del team con priorità, scadenze e assignee.", comingSoon: true },
  { key: "projects",  label: "Projects",  icon: FolderKanban,  href: "/admin/projects",  description: "Progetti attivi, milestone e stato avanzamento.", comingSoon: true },
  { key: "team",      label: "Team",      icon: Users,         href: "/admin/team",      description: "Membri, ruoli, permessi e disponibilità.", comingSoon: true },
  { key: "calendar",  label: "Calendar",  icon: CalendarRange, href: "/admin/calendar",  description: "Agenda condivisa team, eventi e permessi.", comingSoon: true },
  { key: "rating",    label: "Rating",    icon: Star,          href: "/admin/rating",    description: "Valutazioni performance e feedback continuo.", comingSoon: true },
];

const ADMIN_FIN_MODULES: AreaModule[] = [
  { key: "wallet",          label: "Wallet",          icon: Wallet,     href: "/admin/wallet",          description: "Saldi cassa, movimenti finanziari e ledger interno.", comingSoon: true },
  { key: "finance",         label: "Finance",         icon: Banknote,   href: "/admin/finance",         description: "Reporting finanziario, P&L, budget e forecast.", comingSoon: true },
  { key: "banking",         label: "Banking",         icon: Calculator, href: "/admin/banking",         description: "Conti bancari collegati, estratti conto, bonifici.", comingSoon: true },
  { key: "payroll",         label: "Payroll",         icon: HandCoins,  href: "/admin/payroll",         description: "Paghe team, contributi e adempimenti.", comingSoon: true },
  { key: "prima-nota",      label: "Prima Nota",      icon: BookOpen,   href: "/admin/prima-nota",      description: "Registrazioni cronologiche entrate / uscite.", comingSoon: true },
  { key: "libri-giornali",  label: "Libri Giornali",  icon: FileText,   href: "/admin/libri-giornali",  description: "Libro giornale e mastrini contabili.", comingSoon: true },
  { key: "riconciliazioni", label: "Riconciliazioni", icon: Truck,      href: "/admin/riconciliazioni", description: "Riconciliazione bancaria automatica + match prenotazioni.", comingSoon: true },
];

const ADMIN_SYNC_MODULES: AreaModule[] = [
  { key: "sync-center", label: "Sync Center", icon: RefreshCw, href: "/admin/sync-center", description: "Import e sincronizzazioni da gestionali esterni (Aves, Fatture in Cloud)." },
  { key: "alerts",      label: "Alerts",      icon: BellRing,  href: "/admin/alerts",      description: "Notifiche operative e avvisi della suite.", comingSoon: true },
];

// AreaSection.key doubles as the admin SECTION key: it MUST match the CP
// `admin.section.<key>` feature suffix and the booking-engine
// `admin:section:<key>` role suffix (3-level Sync Center permission design).
export const ADMIN_SECTIONS: AreaSection[] = [
  { key: "operations", label: "Operations",         caption: "Tutto quello che arriva da Ops",      description: "I moduli che gestiscono prenotazioni, supplier, contratti e configurazioni travel.", modules: ADMIN_OPS_MODULES },
  { key: "team",       label: "Team & Progetti",    caption: "Persone, attività, calendario · in arrivo", description: "Coordinamento del team, gestione task e progetti, calendario condiviso.",          modules: ADMIN_TEAM_MODULES },
  { key: "financial",  label: "Gestione finanziaria", caption: "Banche · Paghe · Contabilità · in arrivo", description: "Wallet, banche, paghe, prima nota, libri giornali e riconciliazioni.",            modules: ADMIN_FIN_MODULES },
  { key: "sync",       label: "Sync Center",        caption: "Integrazioni gestionali esterni",     description: "Import e sincronizzazioni configurabili da gestionali esterni verso la suite.",      modules: ADMIN_SYNC_MODULES },
];

// Single source for the /admin gating: child route path -> section key.
// Used by the router SectionGuard, the landing and the right dock; a route
// missing here is NOT section-gated (only the area guard applies).
export const ADMIN_ROUTE_SECTIONS: Record<string, string> = {
  catalog: "operations",
  commissions: "operations",
  "contract-room": "operations",
  directory: "operations",
  "directory/:partyId": "operations",
  "directory/:partyId/listini/nuovo": "operations",
  "directory/:partyId/listini/:sheetId": "operations",
  practices: "operations",
  "practices/:practiceId": "operations",
  tasks: "team",
  projects: "team",
  team: "team",
  calendar: "team",
  rating: "team",
  wallet: "financial",
  finance: "financial",
  banking: "financial",
  payroll: "financial",
  "prima-nota": "financial",
  "libri-giornali": "financial",
  riconciliazioni: "financial",
  "sync-center": "sync",
  "sync-center/aves": "sync",
  "sync-center/aves/new": "sync",
  alerts: "alerts",
};

// Route /admin figlia -> entitlement CP richiesto (org_enabled AND
// user_has_access da /me/access). Derivata dalle card del registry
// (requiresEntitlement) + route che non hanno una card propria (il modulo
// Aves vive sotto la card statica Sync Center). Path assente = route non
// gated da entitlement (solo area/section guard). Consumata dal router via
// AreaGuard.entitlement: e' il guard UNIFORME che sana l'incoerenza storica
// (AvesSyncPage si auto-guardava, Pricing/Practices no).
export const ADMIN_ROUTE_ENTITLEMENTS: Record<string, string> = (() => {
  const map: Record<string, string> = {
    "sync-center/aves": "AVES_SYNC",
    "sync-center/aves/new": "AVES_SYNC",
    // Le rotte profonde delle anagrafiche (dettaglio, listini, wizard) NON
    // ereditano il guard della card: la mappa e' per path esatto. Vanno
    // dichiarate a mano, altrimenti /admin/directory/<id> resta non gated.
    "directory/:partyId": "DIRECTORY",
    "directory/:partyId/listini/nuovo": "DIRECTORY",
    "directory/:partyId/listini/:sheetId": "DIRECTORY",
    // stessa ragione: la scheda di una pratica è una rotta profonda, e la
    // mappa è per percorso esatto
    "practices/:practiceId": "PRACTICES",
  };
  for (const section of ADMIN_SECTIONS) {
    for (const module of section.modules) {
      if (module.requiresEntitlement && module.href?.startsWith("/admin/")) {
        map[module.href.slice("/admin/".length)] = module.requiresEntitlement;
      }
    }
  }
  return map;
})();

export const ADMIN_SECTION_KEYS = [
  "operations",
  "team",
  "financial",
  "sync",
  "alerts",
] as const;

// Where to land when redirecting into a section (first module of each).
export const ADMIN_SECTION_HOME: Record<string, string> = {
  operations: "/admin/practices",
  team: "/admin/tasks",
  financial: "/admin/wallet",
  sync: "/admin/sync-center",
  alerts: "/admin/alerts",
};

export const ADMIN_PRIMARY: AreaModule[] = [
  { key: "travel-practice",    label: "Pratiche",           icon: Briefcase,     href: "/admin/practices" },
  { key: "travel-commissions", label: "Travel Commissions", icon: Percent,       href: "/admin/commissions" },
  { key: "directory",          label: "Anagrafiche",        icon: Contact,       href: "/admin/directory" },
];

export const DEV_PRIMARY: AreaModule[] = [
  { key: "auto-connector", label: "Auto Connector",       icon: Plug,     href: "/dev/auto-connector",
    description: "Genera connettori da documentazione fornitore." },
  { key: "ai-mapper",      label: "AI Mapper",            icon: Brain,    href: "/dev/ai-mapper",
    description: "Pipeline di mapping cross-supplier." },
  { key: "runtime",        label: "Runtime Intelligence", icon: Activity, href: "/dev/runtime",
    description: "Dead pool scoring, A/B test, analytics in produzione." },
];

export const OPS_TOPLEVEL: AreaModule[] = [
  { key: "calendar",            label: "Calendar",           icon: Calendar,      href: "/calendar" },
  { key: "analytics",           label: "Analytics",          icon: LineChart,     href: "/analytics" },
  { key: "smart-helper",        label: "Smart AI Helper",    icon: LifeBuoy,      href: "/smart-helper" },
  { key: "agency",              label: "Agency",             icon: Users,         href: "/agency" },
  { key: "supplier-connector",  label: "Supplier Connector", icon: Plug,          href: "/supplier-connector" },
  { key: "system-integration",  label: "System Integration", icon: Cpu,           href: "/system-integration" },
  { key: "distribution",        label: "Distribution",       icon: Network,       href: "/distribution-connection" },
];
